import { toPositiveInteger } from "./currencyUtils";

/**
 * Cálculos centralizados de cotización en pesos chilenos enteros.
 * Evita flotantes y mantiene la lógica tributaria en un solo lugar.
 */

export const DISCOUNT_TYPES = {
	NONE: "none",
	PERCENT: "percent",
	FIXED: "fixed",
};

export const calculateLineDiscountAmount = (cantidad, precioUnitario, tipoDescuento, valorDescuento) => {
	const qty = toPositiveInteger(cantidad);
	const price = toPositiveInteger(precioUnitario);
	const subtotal = qty * price;
	const discountValue = toPositiveInteger(valorDescuento);

	if (tipoDescuento === DISCOUNT_TYPES.PERCENT) {
		const cappedPercent = Math.min(discountValue, 100);
		return Math.min(subtotal, Math.round((subtotal * cappedPercent) / 100));
	}

	if (tipoDescuento === DISCOUNT_TYPES.FIXED) {
		return Math.min(subtotal, discountValue);
	}

	return 0;
};

export const calculateLineTotals = (item = {}) => {
	const cantidad = Math.max(1, toPositiveInteger(item.cantidad) || 1);
	const precioUnitario = toPositiveInteger(item.precioUnitario);
	const tipoDescuento = item.tipoDescuento || DISCOUNT_TYPES.NONE;
	const valorDescuento = toPositiveInteger(item.valorDescuento);
	const subtotal = cantidad * precioUnitario;
	const montoDescuento = calculateLineDiscountAmount(
		cantidad,
		precioUnitario,
		tipoDescuento,
		valorDescuento
	);
	const total = Math.max(0, subtotal - montoDescuento);

	return {
		cantidad,
		precioUnitario,
		tipoDescuento,
		valorDescuento,
		subtotal,
		montoDescuento,
		total,
	};
};

export const calculateQuoteSubtotal = (items = []) => {
	return items.reduce(
		(acc, item) => {
			const line = calculateLineTotals(item);
			return {
				productsSubtotal: acc.productsSubtotal + line.subtotal,
				lineDiscountsTotal: acc.lineDiscountsTotal + line.montoDescuento,
				afterLineDiscounts: acc.afterLineDiscounts + line.total,
			};
		},
		{ productsSubtotal: 0, lineDiscountsTotal: 0, afterLineDiscounts: 0 }
	);
};

export const calculateGlobalDiscount = (afterLineDiscounts, globalDiscount = {}) => {
	const type = globalDiscount.type || DISCOUNT_TYPES.NONE;
	const value = toPositiveInteger(globalDiscount.value);
	const base = toPositiveInteger(afterLineDiscounts);

	if (type === DISCOUNT_TYPES.PERCENT) {
		const cappedPercent = Math.min(value, 100);
		return Math.min(base, Math.round((base * cappedPercent) / 100));
	}

	if (type === DISCOUNT_TYPES.FIXED) {
		return Math.min(base, value);
	}

	return 0;
};

export const calculateTax = (netTotal, taxRate = 0) => {
	const net = Math.max(0, toPositiveInteger(netTotal));
	const rate = toPositiveInteger(taxRate);
	if (rate <= 0) return 0;
	return Math.round((net * rate) / 100);
};

export const calculateGrandTotal = ({ netTotal = 0, taxTotal = 0 }) => {
	return Math.max(0, toPositiveInteger(netTotal) + toPositiveInteger(taxTotal));
};

/**
 * Calcula todos los totales de una cotización según empresa emisora.
 * - afecta: IVA sobre neto después de descuentos
 * - exenta: sin IVA
 */
export const calculateQuoteTotals = ({
	items = [],
	globalDiscount = { type: DISCOUNT_TYPES.NONE, value: 0 },
	tipoTributario = "afecta",
	tasaIva = 19,
}) => {
	const { productsSubtotal, lineDiscountsTotal, afterLineDiscounts } =
		calculateQuoteSubtotal(items);
	const globalDiscountAmount = calculateGlobalDiscount(afterLineDiscounts, globalDiscount);
	const netTotal = Math.max(0, afterLineDiscounts - globalDiscountAmount);
	const isAfecta = tipoTributario === "afecta";
	const taxRate = isAfecta ? toPositiveInteger(tasaIva || 19) : 0;
	const taxTotal = calculateTax(netTotal, taxRate);
	const grandTotal = calculateGrandTotal({ netTotal, taxTotal });

	return {
		subtotal: productsSubtotal,
		lineDiscountsTotal,
		afterLineDiscounts,
		globalDiscountAmount,
		netTotal,
		taxRate,
		taxTotal,
		grandTotal,
		discountsTotal: lineDiscountsTotal + globalDiscountAmount,
	};
};
